import { useState, useEffect, useCallback, useRef } from "react";
import { Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { AudioRecorder } from "./AudioRecorder";
import { ScreenshotCapture } from "./ScreenshotCapture";
import { VideoPlayer } from "@/components/journey/VideoPlayer";

interface FeedbackModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}


export function FeedbackModal({ open, onOpenChange }: FeedbackModalProps) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedbackType, setFeedbackType] = useState<string>("bug");
  const [message, setMessage] = useState("");
  const [screenshotBlob, setScreenshotBlob] = useState<Blob | null>(null);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);

  // Quiet inline how-to link: always available, never in the way.
  const [videoExpanded, setVideoExpanded] = useState(false);
  const requiredWatchPercent = 95;
  const [userId, setUserId] = useState<string | null>(null);
  const [videoProgress, setVideoProgress] = useState(0);
  const initialProgressRef = useRef(0);
  const progressRef = useRef(0);
  const lastSavedIntervalRef = useRef(0);


  // Load progress from backend
  useEffect(() => {
    let cancelled = false;
    let isMounted = true;

    const loadProgress = async () => {
      try {
        const { data: auth } = await supabase.auth.getUser();
        const user = auth.user;
        if (!user || !isMounted) return;
        setUserId(user.id);

        const { data: latest } = await supabase
          .from("content_usage_analytics")
          .select("action_type, metadata, created_at")
          .eq("user_id", user.id)
          .eq("content_type", "feedback_video")
          .eq("content_id", "feedback_modal")
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        const meta = (latest?.metadata as any) || {};
        const loadedPercentRaw =
          latest?.action_type === "complete" ? 100 : Number(meta.watch_percent ?? 0);
        const loadedPercent = Number.isFinite(loadedPercentRaw)
          ? Math.max(0, Math.min(100, loadedPercentRaw))
          : 0;

        if (!isMounted) return;
        initialProgressRef.current = loadedPercent;
        progressRef.current = loadedPercent;
        lastSavedIntervalRef.current = Math.floor(loadedPercent / 5);
        setVideoProgress(loadedPercent);
      } catch (error) {
        console.error("Error loading feedback video progress:", error);
      }
    };

    loadProgress();
    return () => {
      isMounted = false;
    };
  }, []);

  const saveProgress = useCallback(
    async (percent: number) => {
      if (!userId) return;
      try {
        await supabase.from("content_usage_analytics").insert({
          user_id: userId,
          content_type: "feedback_video",
          content_id: "feedback_modal",
          action_type: "progress",
          metadata: { watch_percent: percent },
        });
      } catch (error) {
        console.error("Error saving video progress:", error);
      }
    },
    [userId],
  );

  const handleVideoProgress = useCallback(
    async (percent: number) => {
      progressRef.current = percent;
      setVideoProgress(percent);
      const currentInterval = Math.floor(percent / 5);
      if (currentInterval > lastSavedIntervalRef.current) {
        lastSavedIntervalRef.current = currentInterval;
        await saveProgress(percent);
      }
    },
    [saveProgress],
  );

  const handleVideoComplete = useCallback(async () => {
    if (!userId) return;
    try {
      await supabase.from("content_usage_analytics").insert({
        user_id: userId,
        content_type: "feedback_video",
        content_id: "feedback_modal",
        action_type: "complete",
        metadata: { watch_percent: 100 },
      });
      setVideoExpanded(false);
    } catch (error) {
      console.error("Error saving video completion:", error);
    }
  }, [userId]);

  const resetForm = () => {
    setFeedbackType("bug");
    setMessage("");
    setScreenshotBlob(null);
    setAudioBlob(null);
  };

  const uploadFile = async (blob: Blob, type: "screenshot" | "audio", uid: string) => {
    const extension = type === "screenshot"
      ? (blob.type.includes("jpeg") ? "jpg" : blob.type.includes("gif") ? "gif" : blob.type.includes("webp") ? "webp" : "png")
      : "webm";
    const fileName = `${type}-${Date.now()}.${extension}`;
    const filePath = `${uid}/${fileName}`;

    const { error } = await supabase.storage
      .from("feedback-attachments")
      .upload(filePath, blob, { contentType: blob.type || undefined });

    if (error) throw error;
    return filePath;
  };

  const handleSubmit = async () => {
    if (!message.trim()) {
      toast({
        title: "Add a note first",
        description: "Please tell us what you want to share.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Please sign in before sending feedback.");

      let screenshotPath: string | null = null;
      let audioPath: string | null = null;

      if (screenshotBlob) {
        screenshotPath = await uploadFile(screenshotBlob, "screenshot", user.id);
      }
      if (audioBlob) {
        audioPath = await uploadFile(audioBlob, "audio", user.id);
      }

      const deviceInfo = {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        screenSize: `${window.screen.width}x${window.screen.height}`,
      };

      const { error } = await supabase.functions.invoke("send-feedback", {
        body: {
          userId: user.id,
          userEmail: user.email || null,
          feedbackType,
          message,
          pageUrl: window.location.href,
          deviceInfo,
          screenshotPath,
          audioPath,
        },
      });

      if (error) throw error;


      toast({
        title: "Thanks, we got it",
        description: "Your note is in. We read every one.",
      });

      resetForm();
      onOpenChange(false);
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast({
        title: "That didn't send",
        description: "Something went wrong. Please try again in a moment.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-feedback-ui
      role="dialog"
      aria-label="Send feedback"
      className="fixed z-50 bg-background border rounded-lg shadow-2xl overflow-hidden flex flex-col
                 bottom-2 left-2 right-2 max-h-[85vh]
                 sm:right-auto sm:left-4 sm:bottom-4 sm:w-[420px] sm:max-h-[80vh]"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/40">
        <div>
          <h2 className="text-sm font-semibold">Send feedback</h2>
          <p className="text-xs text-muted-foreground">
            Report a bug, share an idea, or ask us something.
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={() => onOpenChange(false)}
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="p-4 space-y-4 overflow-y-auto">
        <div className="space-y-1.5">
          <button
            type="button"
            onClick={() => setVideoExpanded((v) => !v)}
            className="text-xs text-muted-foreground hover:text-foreground underline decoration-dotted"
            aria-expanded={videoExpanded}
          >
            {videoExpanded ? "Hide the how-to" : "Watch a short how-to"}
          </button>
          {videoExpanded && (
            <div className="rounded-md border overflow-hidden">
              <VideoPlayer
                videoUrl="https://vimeo.com/1152239605"
                requiredWatchPercent={requiredWatchPercent}
                onProgress={handleVideoProgress}
                onComplete={handleVideoComplete}
                initialProgress={videoProgress}
              />
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label>What's this about?</Label>
          <RadioGroup
            value={feedbackType}
            onValueChange={setFeedbackType}
            className="flex flex-wrap gap-3"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="bug" id="fb-bug" />
              <Label htmlFor="fb-bug" className="font-normal cursor-pointer">🐛 Something's broken</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="suggestion" id="fb-suggestion" />
              <Label htmlFor="fb-suggestion" className="font-normal cursor-pointer">💡 An idea</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="question" id="fb-question" />
              <Label htmlFor="fb-question" className="font-normal cursor-pointer">❓ A question</Label>
            </div>
          </RadioGroup>
        </div>

        <div className="space-y-2">
          <Label htmlFor="fb-message">Your note</Label>
          <Textarea
            id="fb-message"
            placeholder="Tell us what you saw or what you'd like to see."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
          />
        </div>

        <ScreenshotCapture
          screenshotBlob={screenshotBlob}
          onScreenshotCapture={setScreenshotBlob}
        />

        <AudioRecorder
          audioBlob={audioBlob}
          onRecordingComplete={setAudioBlob}
        />

        <Button onClick={handleSubmit} disabled={isSubmitting} className="w-full">
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Sending...
            </>
          ) : (
            "Send"
          )}
        </Button>
      </div>
    </div>
  );
}
